import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, Route, Data } from '@angular/router';
import { Observable, Subscription, BehaviorSubject } from "rxjs";
import { tap, switchMap } from 'rxjs/operators';

import { ApplicationBaseComponent } from '../../../applications/components/application-base.component';
import { ApplicationState, WorkflowData, WorkflowNote } from '../../../applications/models/applications.models';
import { ApplicationNotesService } from '../../../applications/services/application-notes.service';
import { ApplicationsService } from '../../../applications/services/applications.service';
import { NoteType } from '../../../common/models/note-type.enum';
import { SidePanel } from '../../../common/models/side-panel.model';
import { Well } from '../../../common/models/well.model';
import { IssuesService } from '../../../common/services/issues.service';
import { LoaderService } from '../../../common/services/loader.service';
import { OrganizationService } from '../../../common/services/organization.service';
import { SidePanelService } from '../../../common/services/side-panel.service';
import { WellService } from '../../../common/services/well.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { SubHeader } from '../../../core/models/sub-header.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { AuthService } from '../../../services/auth.service';
import { ComplianceReportingType, WellSelectionMode } from '../../models/compliance.model';
import { OnBaseDocumentTypeProcessReference } from '../../../common/models/onBaseIntegrationAndMapping.model';

@Component({
    selector: 'app-compliance-submissions-review',
    templateUrl: './compliance-submissions-review.component.html',
    styleUrls: ['./compliance-submissions-review.component.scss'],
    standalone: false
})
export class ComplianceSubmissionsReviewComponent extends ApplicationBaseComponent implements OnInit, OnDestroy {

    application: WorkflowData;
    defaultType: ComplianceReportingType = { typeId: "", description: "--Select--", apiNumbers: WellSelectionMode.None, isAnnual: false, isQuarterly: false } as ComplianceReportingType
	dataLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];
    reportType: ComplianceReportingType;
    get appNumber(): string {
        if (this.application == null) return "";
        return this.application.applicationNumber;
    }

    get apiNumber(): string {
        if (this.application == null) return "";
        let field = this.application.workflowFieldDatas.find(f => f.formControlName.includes('apiNumber'));
        if (field == null) return "";
        return field.stringData;
    }

  
    get applicationState(): ApplicationState {
        if (this.application == null) return ApplicationState.InitialReview;
        return this.application.applicationState;
    }
    get totalDepth(): Date {
     
        if (this.well == null) return null;
        return this.well.dateTotalDepthReached;
	}

	get loadComplete(): boolean {
		if (this.dataLoaded == null) return false;
		return this.dataLoaded.value;
	}

    get wellNumber(): string {
        if (this.well == null) return null;
        let number = this.well.wellNumber;
        if (number != null) return number;
        else return null;
    }



    get wellName(): string {
        if (this.well) {
            let wellName: string = this.well.name;
            if (this.well.lease != null && this.well.lease.name != null && this.well.lease.name.length > 0) wellName = this.well.lease.name;
            else if (this.well.wellNumber != null && this.well.wellNumber.length > 0 && wellName != null && wellName.trim().endsWith(this.well.wellNumber.trim())) {
                wellName = wellName.trim().replace(this.wellNumber.trim(), "");
            }
            return wellName;


        }
        else {
            return null;
        }
    }


    get organization(): any {
        if (this.form == null) return null;
        let ctrl = this.form.get('organization');
        if (ctrl == null) return null;
        let id = this.form.get('organization').value;
        let name = this.form.get('organizationName').value;
        let rbdms = this.form.get('rbdmsNumber').value;
        return {
            id: id,
            name: name,
            historicalOwnerNumber: rbdms
        };
    }

    get orgId(): string {
        if (this.application != null) { let id = this.application.organizationId; return id; }
        return ""
        
    }

    get urlToWellDetails(): string {
        return `common/organization/${this.orgId}/well/${this.apiNumber}`;
    }

    isInReview: boolean;
    reportTypes: ComplianceReportingType[] = [];

    reviewStates: ApplicationState[] = [
        ApplicationState.VerifyCompletion,
        ApplicationState.InitialReview,
        ApplicationState.InitialApproval,
        ApplicationState.FinalApproval
    ];


    subHeader: SubHeader = new SubHeader({ title: "", buttons: [] });

    get subheaderText(): string {
        return "Compliance Report #[XXX]";
    }

  
    noTotalDepthDialog: Dialog = new Dialog("There must be a TD date in database in order to proceed. Please process the Form 8 first or request it from the RICS owner.",
        [new DialogButton("I understand.", true, () => { this.home(); })]);

    well: Well;

    constructor(public loader: LoaderService, private activatedRoute: ActivatedRoute, public router: Router, auth: AuthService, dialogService: DynamicDialogService,
        private issuesService: IssuesService, private builder: FormBuilder, private organizationService: OrganizationService, public appService: ApplicationsService, _notesService: ApplicationNotesService, _subheader: SubHeaderService, _sidePanelService: SidePanelService, public wellService: WellService) {
        super(loader, dialogService, _subheader, auth, appService, router, _sidePanelService, _notesService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.form = this.buildForm();
        this.isInReview = true;

        let type$ = this.issuesService.getComplianceReportingTypes(true).pipe(
            tap((data: ComplianceReportingType[]) => {
                let sorted = data.sort((a, b) => {
                    if (a.description > b.description) return 1;
                    if (b.description > a.description) return -1;
                    return 0;
                });
               sorted.forEach(s => { this.reportTypes.push(s); });
                this.reportTypes.unshift(this.defaultType);
            })
        )

        let route$: Observable<Data> = this.activatedRoute.data.pipe(
            tap((data: Data) => {
                this.application = data.application;
                this.onBaseDocumentTypes = data.documentTypes;
                this.prefillForm();
            })
        );        

        let subs$: Subscription = route$.pipe(
            switchMap(() => {
                return this.wellService.getWellById(this.apiNumber).pipe(
                    tap((data: Well) => {
                        this.well = data;
                    })
                )
            }),
            switchMap(() => {
                return type$;
            })            
        ).subscribe(
            () => {
                this.subHeader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
                this.subHeader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });
                this.subheaderService.updateSubHeader(this.subHeader);

				this.dataLoaded.next(true);
                this.reportType = this.reportTypes.find(f => f.typeId == this.application.workflowFieldDatas.find(f => f.formControlName == 'reportType').stringData);
                if (this.reportType.typeId == "LOGD" && this.application.workflowFieldDatas.find(f => f.formControlName == 'confidentiality').booleanData == true && this.totalDepth == null) {
                    this.noTotalDepthDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.noTotalDepthDialog);
                }
            
                },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
            );
        this.subscription.add(subs$);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    buildForm(): FormGroup {
        return this.builder.group({
            apiNumber: [{ value: this.apiNumber }, []],
            selectedWell: [{ value: this.apiNumber }, []],
            organizationName: [{value: "", disabled: true }, []],
            organization: [{value: "", disabled: true }, []],
            rbdmsNumber: [{value: "", disabled: true }, []],
            totalDepthDate: [{ value: "", disabled: true }, []],
            dueDate: [null, []]
        });
    }

    prefillForm(): void {
        let orgName = this.application.workflowFieldDatas.find(f => f.formControlName == "organizationName");
        if (orgName != null) this.form.get('organizationName').setValue(orgName.stringData);
        let orgId = this.application.workflowFieldDatas.find(f => f.formControlName == "organization");
        if (orgId != null) this.form.get('organization').setValue(orgId.stringData);
        let rbdms = this.application.workflowFieldDatas.find(f => f.formControlName == "rbdmsNumber");
        if (rbdms != null) this.form.get('rbdmsNumber').setValue(rbdms.stringData);

    }

    logError(error: any): void {
        console.error(error);
    }

    routeToWellDetails(): void {
        this.router.navigate([`common/organization/${this.organization.id}/well/${this.apiNumber}`]);
      
    }

}

